import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Loader2, Plus, BookOpen, Wand2, Check, RefreshCw, PlayCircle, Wand, Brain, Target, BookCheck, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Question } from "@/types/quiz"; 
import LanguageExplanation from "@/components/mock-test/LanguageExplanation";
import MathRenderer from "@/components/mock-test/MathRenderer";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuestionInputData {
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctOption: string;
  explanation?: string;
  passageText?: string;
  questionType: 'standard' | 'comprehension' | 'data_interpretation' | 'puzzle';
  autoGenerateOptions: boolean;
  topic?: string;
  difficulty?: string;
}

interface GeneratedVariation {
  id: string;
  question_text: string;
  category: string;
  difficulty: string;
  topic?: string;
}

interface ExamCategory {
  id: number;
  name: string;
  type: string;
  question_count: number;
  time_minutes: number;
  description: string;
  has_sections: boolean;
  sections: any[];
}

interface ExamSection {
  name: string;
  questions: number;
  time_minutes: number;
}

interface PerformanceMetric {
  topic: string;
  accuracy: number;
  attempts: number;
  avgTimeSeconds: number;
}

interface PYQMultiplierProps {
  onQuestionsGenerated?: (questions: Question[]) => void;
}

const PYQMultiplier: React.FC<PYQMultiplierProps> = ({ onQuestionsGenerated }) => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { isMobile, isSmallMobile, screenSize } = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [examCategories, setExamCategories] = useState<ExamCategory[]>([]);
  const [selectedExamCategory, setSelectedExamCategory] = useState<ExamCategory | null>(null);
  const [selectedSection, setSelectedSection] = useState<ExamSection | null>(null);
  const [numVariations, setNumVariations] = useState<number>(3);
  const [examName, setExamName] = useState<string>("AI Generated Mock Test");
  const [sectionName, setSectionName] = useState<string>("General");
  const [generatedVariations, setGeneratedVariations] = useState<GeneratedVariation[]>([]);
  const [showInputForm, setShowInputForm] = useState<boolean>(true);
  const [userPerformance, setUserPerformance] = useState<PerformanceMetric[]>([]);
  const [recommendedDifficulty, setRecommendedDifficulty] = useState<string>('medium');
  const [activeTab, setActiveTab] = useState<string>("generate");
  
  // New states for attempting all questions at once
  const [attemptMode, setAttemptMode] = useState<'single' | 'all' | 'review'>('single');
  const [questionsToAttempt, setQuestionsToAttempt] = useState<Question[]>([]);
  const [userAnswers, setUserAnswers] = useState<(number | null)[]>([]);
  const [score, setScore] = useState<number>(0);
  const [showExplanations, setShowExplanations] = useState<boolean>(false);
  
  // New state for direct question input with added topic field
  const [questionInput, setQuestionInput] = useState<QuestionInputData>({
    questionText: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctOption: 'A',
    explanation: '',
    passageText: '',
    questionType: 'standard',
    autoGenerateOptions: false,
    topic: '',
    difficulty: 'medium'
  });

  useEffect(() => {
    fetchExamCategories();
    if (session?.user) {
      fetchUserPerformance();
    }
  }, [session?.user]);

  // New function to fetch user performance data
  const fetchUserPerformance = async () => {
    try {
      const { data: performanceData, error } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', session?.user?.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        throw error;
      }

      // Process performance data to get metrics by topic
      const topicPerformance: Record<string, {correct: number, total: number, time: number, attempts: number}> = {};
      
      performanceData?.forEach(record => {
        if (!record.subject) return;
        
        if (!topicPerformance[record.subject]) {
          topicPerformance[record.subject] = {
            correct: 0,
            total: 0,
            time: 0,
            attempts: 0
          };
        }
        
        topicPerformance[record.subject].correct += record.correct_answers || 0;
        topicPerformance[record.subject].total += record.total_questions || 0;
        topicPerformance[record.subject].attempts += 1;
      });
      
      // Convert to array of metrics
      const metrics = Object.entries(topicPerformance).map(([topic, data]) => ({
        topic,
        accuracy: data.total > 0 ? (data.correct / data.total) * 100 : 0,
        attempts: data.attempts,
        avgTimeSeconds: data.attempts > 0 ? data.time / data.attempts : 0
      }));
      
      setUserPerformance(metrics);
      
      // Calculate recommended difficulty based on overall performance
      const totalCorrect = performanceData?.reduce((sum, record) => sum + (record.correct_answers || 0), 0);
      const totalQuestions = performanceData?.reduce((sum, record) => sum + (record.total_questions || 0), 0);
      
      if (totalQuestions && totalQuestions > 0) {
        const overallAccuracy = (totalCorrect || 0) / totalQuestions * 100;
        
        if (overallAccuracy > 80) {
          setRecommendedDifficulty('hard');
        } else if (overallAccuracy > 50) {
          setRecommendedDifficulty('medium');
        } else {
          setRecommendedDifficulty('easy');
        }
        
        setQuestionInput(prev => ({
          ...prev,
          difficulty: overallAccuracy > 80 ? 'hard' : overallAccuracy > 50 ? 'medium' : 'easy'
        }));
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    }
  };

  const fetchExamCategories = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exam_categories')
        .select('*')
        .order('name');

      if (error) {
        throw error;
      }

      // Process exam categories and ensure sections are properly parsed
      const processedData = data?.map(item => {
        let sections = item.sections || [];
        
        if (typeof sections === 'string') {
          try {
            sections = JSON.parse(sections);
          } catch (e) {
            console.error('Error parsing sections JSON:', e);
            sections = [];
          }
        }
        
        return {
          ...item,
          sections
        };
      }) || [];
      
      setExamCategories(processedData);
      console.log('Exam categories loaded:', processedData);
    } catch (error) {
      console.error('Error fetching exam categories:', error);
      toast.error("Failed to fetch exam categories");
    } finally {
      setLoading(false);
    }
  };

  const handleExamCategorySelect = (categoryId: string) => {
    const examCategory = examCategories.find(cat => cat.id.toString() === categoryId);
    setSelectedExamCategory(examCategory || null);
    
    if (examCategory) {
      setExamName(examCategory.name);
      // Reset section when changing exam category
      setSelectedSection(null);
    }
  };

  const handleSectionSelect = (sectionName: string) => {
    if (selectedExamCategory) {
      const section = selectedExamCategory.sections.find(s => s.name === sectionName);
      setSelectedSection(section || null);
      
      if (section) {
        setSectionName(section.name);
      }
    }
  };

  const handleQuestionInputChange = (field: keyof QuestionInputData, value: string | boolean) => {
    setQuestionInput(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const handleGenerateVariations = async () => {
    if (!session?.user) {
      toast.error("Please log in to generate questions");
      return;
    }

    // Validate input - only question text is required if auto-generating options
    if (!questionInput.questionText) {
      toast.error("Please fill in at least the question text");
      return;
    }
    
    // If not auto-generating options, validate options are provided
    if (!questionInput.autoGenerateOptions) {
      if (!questionInput.optionA || !questionInput.optionB || 
          !questionInput.optionC || !questionInput.optionD || !questionInput.correctOption) {
        toast.error("When not auto-generating options, please fill in all options and select a correct option");
        return;
      }
    }

    if (!selectedExamCategory) {
      toast.error("Please select an exam category");
      return;
    }

    setGenerating(true);
    toast.info("Generating variations...", { duration: 3000 });
    
    try {
      // Prepare the options array - only if not auto-generating
      const options = questionInput.autoGenerateOptions ? [] : [
        questionInput.optionA,
        questionInput.optionB, 
        questionInput.optionC,
        questionInput.optionD
      ];
      
      // Use selected section name if available, otherwise use the default
      const effectiveSectionName = selectedSection ? selectedSection.name : sectionName;
      // Use selected exam name if available, otherwise use the default
      const effectiveExamName = selectedExamCategory ? selectedExamCategory.name : examName;
      
      console.log('Calling pyq-multiplier with:', {
        question: questionInput.questionText,
        options: options,
        autoGenerateOptions: questionInput.autoGenerateOptions,
        examName: effectiveExamName,
        sectionName: effectiveSectionName,
        topic: questionInput.topic,
        difficulty: questionInput.difficulty
      });
      
      // Call the edge function to generate variations
      const response = await supabase.functions.invoke('pyq-multiplier', {
        body: {
          question: questionInput.questionText,
          options: options,
          correctAnswer: questionInput.autoGenerateOptions ? '' : questionInput.correctOption,
          numVariations: numVariations,
          examName: effectiveExamName,
          sectionName: effectiveSectionName,
          passageText: questionInput.passageText,
          questionType: questionInput.questionType,
          explanation: questionInput.explanation,
          autoGenerateOptions: questionInput.autoGenerateOptions,
          topic: questionInput.topic, // Added topic to the request
          difficulty: questionInput.difficulty // Send difficulty preference
        }
      });
      
      console.log('Generation response:', response);
      
      if (response.error) {
        console.error('Edge function error:', response.error);
        throw new Error(response.error.message || "Failed to generate variations");
      }
      
      if (!response.data) {
        throw new Error("No data returned from the edge function");
      }
      
      if (response.data.success) {
        toast.success(`Successfully generated ${response.data.variationIds?.length || 0} questions`);
        
        // Fetch the newly generated variations
        if (response.data.variationIds && response.data.variationIds.length > 0) {
          const { data: newVariations, error } = await supabase
            .from('mock_test_questions')
            .select('id, question_text, category, difficulty, metadata')
            .in('id', response.data.variationIds)
            .order('created_at', { ascending: false });
            
          if (!error && newVariations) {
            // Update the generated variations state
            setGeneratedVariations(prevVariations => [
              ...(newVariations.map(v => ({
                id: v.id,
                question_text: v.question_text,
                category: v.category,
                difficulty: v.difficulty,
                topic: v.metadata?.topic || questionInput.topic || '' // Extract topic from metadata
              }))),
              ...prevVariations
            ]);
          } else if (error) {
            console.error('Error fetching new variations:', error);
            toast.error("Generated questions but couldn't fetch the details");
          }
        } else {
          toast.warning("No variations were generated");
        }
      } else {
        const errorMsg = response.data.error || "Unknown error generating variations";
        console.error('Error in response data:', errorMsg);
        toast.error(errorMsg);
        
        // If there are additional details, show them
        if (response.data.details) {
          console.error('Error details:', response.data.details);
        }
        
        if (response.data.rawResponse) {
          console.log('Raw AI response for debugging:', response.data.rawResponse);
        }
      }
    } catch (error) {
      console.error('Error generating variations:', error);
      toast.error("Failed to generate variations: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setGenerating(false);
    }
  };
  
  const handleAttemptVariation = (variationId: string) => {
    if (!session?.user) {
      toast.error("Please log in to attempt questions");
      return;
    }
    
    // Create a mock test configuration with just this one question
    const mockTestConfig = {
      sessionId: `single_question_${variationId}`,
      startTime: new Date().toISOString(),
      timeLimit: 5, // 5 minutes for a single question
      testTitle: "Single Question Practice",
      question_count: 1,
      time_minutes: 5,
      category: selectedExamCategory?.name || "General"
    };
    
    // Fetch the question and create a mock test
    const createSingleQuestionTest = async () => {
      try {
        // Get the question details
        const { data: questionData, error } = await supabase
          .from('mock_test_questions')
          .select('*')
          .eq('id', variationId)
          .single();
          
        if (error) throw error;
        if (!questionData) {
          toast.error("Question not found");
          return;
        }
        
        // Transform the question to the expected format
        const transformedQuestion = {
          id: questionData.id,
          question: questionData.question_text,
          options: [
            questionData.option_a,
            questionData.option_b,
            questionData.option_c,
            questionData.option_d
          ],
          correctanswer: ["A", "B", "C", "D"].indexOf(questionData.correct_option),
          difficulty: questionData.difficulty,
          category: questionData.category,
          subject: questionData.subject || questionData.category,
          explanation: questionData.explanation,
          marks: questionData.marks || 1,
          negative_marks: questionData.negative_marks || 0.25,
          question_type: 'mcq',
          metadata: questionData.metadata || {}
        };
        
        // Store the test config in localStorage
        const fullConfig = {
          ...mockTestConfig,
          questions: [transformedQuestion]
        };
        
        localStorage.setItem('mockTest_config', JSON.stringify(fullConfig));
        
        // Generate a unique test ID based on timestamp
        const testId = `practice_${Date.now()}`;
        localStorage.setItem(`mockTest_${testId}`, JSON.stringify(fullConfig));
        
        // Navigate to the mock test page
        navigate(`/mock-test/${testId}`);
        
      } catch (error) {
        console.error('Error creating practice test:', error);
        toast.error("Failed to start practice test");
      }
    };
    
    createSingleQuestionTest();
  };
  
  const handleCreateMockTest = async () => {
    if (!session?.user) {
      toast.error("Please log in to create a mock test");
      return;
    }
    
    if (generatedVariations.length === 0) {
      toast.error("No variations available to create a mock test. Please generate some variations first.");
      return;
    }
    
    try {
      setLoading(true);
      
      // Get all variation IDs
      const allVariationIds = generatedVariations.map(v => v.id);
      
      if (allVariationIds.length === 0) {
        toast.error("No variations available to create a mock test. Please generate some variations first.");
        setLoading(false);
        return;
      }
      
      // Get all questions
      const { data: questionData, error } = await supabase
        .from('mock_test_questions')
        .select('*')
        .in('id', allVariationIds)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      if (!questionData || questionData.length === 0) {
        toast.error("No questions found");
        setLoading(false);
        return;
      }
      
      // Transform the questions to the expected format
      const transformedQuestions = questionData.map(q => ({
        id: q.id,
        question: q.question_text,
        options: [q.option_a, q.option_b, q.option_c, q.option_d],
        correctanswer: ["A", "B", "C", "D"].indexOf(q.correct_option),
        difficulty: q.difficulty,
        category: q.category,
        subject: q.subject || q.category,
        explanation: q.explanation,
        marks: q.marks || 1,
        negative_marks: q.negative_marks || 0.25,
        question_type: 'mcq',
        metadata: q.metadata || {}
      }));
      
      // Create a mock test configuration
      const mockTestConfig = {
        sessionId: `generated_${Date.now()}`,
        startTime: new Date().toISOString(),
        timeLimit: Math.min(60, questionData.length * 2), // 2 minutes per question, max 60 minutes
        questions: transformedQuestions,
        category: selectedExamCategory?.name || "General Exam",
        testTitle: `${examName} - ${selectedSection?.name || sectionName}`,
        question_count: transformedQuestions.length,
        time_minutes: Math.min(60, questionData.length * 2)
      };
      
      // Store the test config in localStorage
      localStorage.setItem('mockTest_config', JSON.stringify(mockTestConfig));
      
      // Generate a unique test ID based on timestamp
      const testId = `generated_${Date.now()}`;
      localStorage.setItem(`mockTest_${testId}`, JSON.stringify(mockTestConfig));
      
      toast.success("Mock test created successfully!");
      
      // Navigate to the mock test page
      navigate(`/mock-test/${testId}`);
      
    } catch (error) {
      console.error('Error creating mock test:', error);
      toast.error("Failed to create mock test");
    } finally {
      setLoading(false);
    }
  };

  // New function to get a topic color based on performance
  const getTopicPerformanceColor = (topic: string): string => {
    const performanceRecord = userPerformance.find(p => p.topic.toLowerCase() === topic.toLowerCase());
    if (!performanceRecord) return "bg-blue-100 text-blue-800";
    
    if (performanceRecord.accuracy >= 80) return "bg-green-100 text-green-800";
    if (performanceRecord.accuracy >= 50) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  // New function to get topic suggestions based on performance
  const getTopicSuggestions = (): string[] => {
    // Sort topics by accuracy (ascending) to focus on weaker areas
    return userPerformance
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, 3)
      .map(p => p.topic);
  };

  const renderQuestionInputForm = () => {
    return (
      <Card className="mb-6">
        <CardHeader className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
          <CardTitle className={`${isSmallMobile ? 'text-base' : ''}`}>Input Question</CardTitle>
          <CardDescription className={`${isSmallMobile ? 'text-xs' : ''}`}>
            Enter your question details below. Options can be auto-generated or provided manually.
          </CardDescription>
        </CardHeader>
        <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
          <div className="space-y-4">
            {userPerformance.length > 0 && (
              <div className="bg-muted/20 p-2 sm:p-4 rounded-lg border border-dashed">
                <div className="flex items-center mb-2">
                  <Brain className="h-4 w-4 mr-2 text-primary" />
                  <h3 className={`font-medium ${isSmallMobile ? 'text-sm' : ''}`}>Adaptive Recommendations</h3>
                </div>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center">
                    <Target className="h-4 w-4 mr-2 text-primary" />
                    <span className={`${isSmallMobile ? 'text-xs' : 'text-sm'}`}>Recommended difficulty: </span> 
                    <Badge variant="outline" className="ml-2">{recommendedDifficulty}</Badge>
                  </div>
                  <div>
                    <span className={`${isSmallMobile ? 'text-xs' : 'text-sm'}`}>Focus areas: </span> 
                    <div className="flex flex-wrap gap-1 mt-1">
                      {getTopicSuggestions().map((topic, index) => (
                        <Badge 
                          key={index} 
                          className={getTopicPerformanceColor(topic)}
                          onClick={() => handleQuestionInputChange('topic', topic)}
                        >
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="questionType" className={`${isSmallMobile ? 'text-xs' : ''}`}>Question Type</Label>
              <Select 
                value={questionInput.questionType} 
                onValueChange={(val) => handleQuestionInputChange('questionType', val as any)}
              >
                <SelectTrigger className={`${isSmallMobile ? 'text-xs h-8' : ''}`}>
                  <SelectValue placeholder="Select question type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard MCQ</SelectItem>
                  <SelectItem value="comprehension">Reading Comprehension</SelectItem>
                  <SelectItem value="data_interpretation">Data Interpretation</SelectItem>
                  <SelectItem value="puzzle">Puzzle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="topic" className={`${isSmallMobile ? 'text-xs' : ''}`}>Topic</Label>
              <Input
                id="topic"
                placeholder="Enter topic (e.g., Newton's Laws, Algebra, etc.)"
                value={questionInput.topic}
                onChange={(e) => handleQuestionInputChange('topic', e.target.value)}
                className={`mt-1 ${isSmallMobile ? 'text-xs h-8' : ''}`}
              />
            </div>
            
            <div>
              <Label htmlFor="difficulty" className={`${isSmallMobile ? 'text-xs' : ''}`}>Difficulty</Label>
              <Select 
                value={questionInput.difficulty} 
                onValueChange={(val) => handleQuestionInputChange('difficulty', val)}
              >
                <SelectTrigger className={`${isSmallMobile ? 'text-xs h-8' : ''}`}>
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(questionInput.questionType !== 'standard') && (
              <div>
                <Label htmlFor="passageText" className={`${isSmallMobile ? 'text-xs' : ''}`}>
                  {questionInput.questionType === 'comprehension' ? 'Reading Passage' : 
                   questionInput.questionType === 'data_interpretation' ? 'Data Information' : 'Puzzle Description'}
                </Label>
                <Textarea
                  id="passageText"
                  placeholder={
                    questionInput.questionType === 'comprehension' ? 'Enter passage text here...' : 
                    questionInput.questionType === 'data_interpretation' ? 'Enter data description, tables, charts information...' : 
                    'Enter puzzle description...'
                  }
                  value={questionInput.passageText}
                  onChange={(e) => handleQuestionInputChange('passageText', e.target.value)}
                  className={`mt-1 min-h-[150px] ${isSmallMobile ? 'text-xs min-h-[100px]' : ''}`}
                />
                <p className={`mt-1 ${isSmallMobile ? 'text-[10px]' : 'text-xs'} text-muted-foreground`}>
                  LaTeX supported, use $ for inline math and $$ for block equations
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="questionText" className={`${isSmallMobile ? 'text-xs' : ''}`}>Question</Label>
              <Textarea
                id="questionText"
                placeholder="Enter your question text here"
                value={questionInput.questionText}
                onChange={(e) => handleQuestionInputChange('questionText', e.target.value)}
                className={`mt-1 ${isSmallMobile ? 'text-xs min-h-[80px]' : ''}`}
              />
              <p className={`mt-1 ${isSmallMobile ? 'text-[10px]' : 'text-xs'} text-muted-foreground`}>
                LaTeX supported, use $ for inline math and $$ for block equations
              </p>
            </div>

            <div className="flex items-center space-x-2 my-4">
              <Switch 
                checked={questionInput.autoGenerateOptions}
                onCheckedChange={(checked) => handleQuestionInputChange('autoGenerateOptions', checked)}
                id="auto-generate-options"
              />
              <Label htmlFor="auto-generate-options" className={`flex items-center cursor-pointer ${isSmallMobile ? 'text-xs' : ''}`}>
                <Wand className={`mr-2 ${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} /> 
                Auto-generate options
              </Label>
            </div>
            
            {!questionInput.autoGenerateOptions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="optionA" className={`${isSmallMobile ? 'text-xs' : ''}`}>Option A</Label>
                  <Input
                    id="optionA"
                    placeholder="Option A"
                    value={questionInput.optionA}
                    onChange={(e) => handleQuestionInputChange('optionA', e.target.value)}
                    className={`mt-1 ${isSmallMobile ? 'text-xs h-8' : ''}`}
                  />
                </div>
                
                <div>
                  <Label htmlFor="optionB" className={`${isSmallMobile ? 'text-xs' : ''}`}>Option B</Label>
                  <Input
                    id="optionB"
                    placeholder="Option B"
                    value={questionInput.optionB}
                    onChange={(e) => handleQuestionInputChange('optionB', e.target.value)}
                    className={`mt-1 ${isSmallMobile ? 'text-xs h-8' : ''}`}
                  />
                </div>
                
                <div>
                  <Label htmlFor="optionC" className={`${isSmallMobile ? 'text-xs' : ''}`}>Option C</Label>
                  <Input
                    id="optionC"
                    placeholder="Option C"
                    value={questionInput.optionC}
                    onChange={(e) => handleQuestionInputChange('optionC', e.target.value)}
                    className={`mt-1 ${isSmallMobile ? 'text-xs h-8' : ''}`}
                  />
                </div>
                
                <div>
                  <Label htmlFor="optionD" className={`${isSmallMobile ? 'text-xs' : ''}`}>Option D</Label>
                  <Input
                    id="optionD"
                    placeholder="Option D"
                    value={questionInput.optionD}
                    onChange={(e) => handleQuestionInputChange('optionD', e.target.value)}
                    className={`mt-1 ${isSmallMobile ? 'text-xs h-8' : ''}`}
                  />
                </div>

                <div>
                  <Label htmlFor="correctOption" className={`${isSmallMobile ? 'text-xs' : ''}`}>Correct Option</Label>
                  <Select 
                    value={questionInput.correctOption} 
                    onValueChange={(val) => handleQuestionInputChange('correctOption', val)}
                  >
                    <SelectTrigger className={`${isSmallMobile ? 'text-xs h-8' : ''}`}>
                      <SelectValue placeholder="Select correct option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Option A</SelectItem>
                      <SelectItem value="B">Option B</SelectItem>
                      <SelectItem value="C">Option C</SelectItem>
                      <SelectItem value="D">Option D</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="explanation" className={`${isSmallMobile ? 'text-xs' : ''}`}>Explanation (Optional)</Label>
              <Textarea
                id="explanation"
                placeholder="Explanation for the correct answer"
                value={questionInput.explanation || ''}
                onChange={(e) => handleQuestionInputChange('explanation', e.target.value)}
                className={`mt-1 ${isSmallMobile ? 'text-xs min-h-[80px]' : ''}`}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // New component to render the user's performance metrics
  const renderPerformanceMetrics = () => {
    if (userPerformance.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No performance data available yet.</p>
          <p className="text-sm">Complete some quizzes to see your metrics here.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Topic Performance</h3>
        {userPerformance.map((metric, index) => (
          <div key={index} className="bg-card p-4 rounded-lg border">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">{metric.topic}</h4>
              <Badge variant={metric.accuracy >= 80 ? "success" : metric.accuracy >= 50 ? "warning" : "destructive"}>
                {Math.round(metric.accuracy)}%
              </Badge>
            </div>
            <Progress value={metric.accuracy} className="h-2 mb-2" />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{metric.attempts} attempts</span>
              <span
                className="cursor-pointer underline"
                onClick={() => {
                  setQuestionInput(prev => ({...prev, topic: metric.topic}));
                  setActiveTab("generate");
                }}
              >
                Generate questions for this topic
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // New function to render questions in attempt all mode
  const renderQuestionsToAttempt = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <h3 className="text-lg font-medium">Answer All Questions</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBackToGeneration}
          >
            Back to Generation
          </Button>
        </div>
        
        {questionsToAttempt.map((question, qIndex) => (
          <Card key={question.id} className="mb-4">
            <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-4'}`}>
              <div className="mb-2">
                <span className="font-medium mr-2">Q{qIndex + 1}:</span>
                <MathRenderer text={question.question} />
              </div>
              <div className="space-y-2">
                {question.options.map((option, oIndex) => (
                  <div key={oIndex} className="flex items-center">
                    <input
                      type="radio"
                      id={`q${qIndex}_o${oIndex}`}
                      name={`question_${qIndex}`}
                      checked={userAnswers[qIndex] === oIndex}
                      onChange={() => {
                        const newAnswers = [...userAnswers];
                        newAnswers[qIndex] = oIndex;
                        setUserAnswers(newAnswers);
                      }}
                      className="mr-2"
                    />
                    <label 
                      htmlFor={`q${qIndex}_o${oIndex}`}
                      className={`${isSmallMobile ? 'text-sm' : ''}`}
                    >
                      <MathRenderer text={option} />
                    </label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        
        <div className="flex justify-between mt-4">
          <Button 
            variant="outline" 
            onClick={() => setShowExplanations(!showExplanations)}
          >
            {showExplanations ? "Hide Explanations" : "Show Explanations"}
          </Button>
          <Button onClick={handleSubmitAllAnswers}>
            Submit All Answers
          </Button>
        </div>
      </div>
    );
  };

  // Function to handle back to generation
  const handleBackToGeneration = () => {
    setAttemptMode('single');
    setActiveTab('generate');
  };

  // Function to handle submit all answers
  const handleSubmitAllAnswers = () => {
    let correctCount = 0;
    questionsToAttempt.forEach((question, index) => {
      if (userAnswers[index] === question.correctanswer) {
        correctCount++;
      }
    });
    
    setScore(correctCount);
    setShowExplanations(true);
    setAttemptMode('review');
  };

  return (
    <div className={`container mx-auto ${isMobile ? 'px-2 pb-24' : 'p-4'} max-w-6xl`}>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className={`${isMobile ? 'w-full' : ''}`}>
          <TabsTrigger value="generate" className={`flex items-center gap-2 ${isSmallMobile ? 'text-xs' : ''}`}>
            <Wand className={`${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
            Generate
          </TabsTrigger>
          <TabsTrigger value="performance" className={`flex items-center gap-2 ${isSmallMobile ? 'text-xs' : ''}`}>
            <Target className={`${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
            Performance
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-4">
          {attemptMode === 'single' && (
            <>
              {showInputForm ? (
                <div>
                  <div className={`flex justify-between items-center mb-4 ${isMobile ? 'flex-col space-y-2 items-start' : ''}`}>
                    <h2 className={`font-bold ${isSmallMobile ? 'text-lg' : 'text-xl'}`}>
                      Generate Similar Questions
                    </h2>
                    <div className="flex items-center">
                      <Label htmlFor="num-variations" className={`mr-2 ${isSmallMobile ? 'text-xs' : ''}`}>
                        Variations:
                      </Label>
                      <Select
                        value={numVariations.toString()}
                        onValueChange={(value) => setNumVariations(parseInt(value))}
                      >
                        <SelectTrigger className={`w-[100px] ${isSmallMobile ? 'text-xs h-8' : ''}`}>
                          <SelectValue placeholder="3" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <Label htmlFor="exam-category" className={`${isSmallMobile ? 'text-xs' : ''}`}>Exam Category</Label>
                    <div className={`mt-1 ${isMobile ? 'space-y-2' : 'grid grid-cols-2 gap-4'}`}>
                      <Select onValueChange={handleExamCategorySelect}>
                        <SelectTrigger className={`${isSmallMobile ? 'text-xs h-8' : ''}`}>
                          <SelectValue placeholder="Select exam category" />
                        </SelectTrigger>
                        <SelectContent>
                          {examCategories.map((category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {selectedExamCategory?.has_sections && (
                        <Select onValueChange={handleSectionSelect}>
                          <SelectTrigger className={`${isSmallMobile ? 'text-xs h-8' : ''}`}>
                            <SelectValue placeholder="Select section" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedExamCategory.sections.map((section) => (
                              <SelectItem key={section.name} value={section.name}>
                                {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {renderQuestionInputForm()}

                  <div className="flex flex-wrap gap-2 items-center justify-center mt-6">
                    <Button
                      onClick={handleGenerateVariations}
                      disabled={generating || loading}
                      className={`${isMobile ? 'flex-grow' : ''}`}
                    >
                      {generating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className={`mr-2 ${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                          Generate Similar Questions
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button onClick={() => setShowInputForm(true)} variant="outline">
                    <Plus className="mr-2 h-4 w-4" /> New Question
                  </Button>
                </div>
              )}

              {generatedVariations.length > 0 && (
                <Card className="mt-8">
                  <CardHeader className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
                    <CardTitle className={`flex items-center justify-between ${isSmallMobile ? 'text-base' : ''}`}>
                      <span>Generated Variations</span>
                      <Button onClick={handleCreateMockTest} className={`${isSmallMobile ? 'text-xs py-1 px-2 h-auto' : ''}`}>
                        <BookOpen className={`mr-2 ${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} /> Create Mock Test
                      </Button>
                    </CardTitle>
                    <CardDescription className={`${isSmallMobile ? 'text-xs' : ''}`}>
                      Click on any question to attempt it or create a mock test with all questions.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
                    <div className="space-y-2">
                      {generatedVariations.map((variation) => (
                        <Card key={variation.id} className="mb-4 hover:shadow-md transition-shadow">
                          <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-4'}`}>
                            <div className={`flex justify-between items-start ${isMobile ? 'flex-col gap-2' : ''}`}>
                              <div className="flex-grow">
                                <div className={`font-medium ${isSmallMobile ? 'text-xs' : ''}`}>
                                  <MathRenderer text={variation.question_text} />
                                </div>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <Badge variant="outline" className={`${isSmallMobile ? 'text-[10px] py-0' : 'text-xs'}`}>
                                    {variation.category}
                                  </Badge>
                                  <Badge variant="outline" className={`${isSmallMobile ? 'text-[10px] py-0' : 'text-xs'}`}>
                                    {variation.difficulty}
                                  </Badge>
                                  {variation.topic && (
                                    <Badge 
                                      className={`${getTopicPerformanceColor(variation.topic)} ${isSmallMobile ? 'text-[10px] py-0' : 'text-xs'}`}
                                    >
                                      {variation.topic}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className={`${isMobile ? 'w-full flex justify-end' : ''}`}>
                                <Button 
                                  size={isSmallMobile ? "sm" : "default"}
                                  onClick={() => handleAttemptVariation(variation.id)}
                                  className={`${isSmallMobile ? 'text-xs py-1 px-2 h-auto' : ''}`}
                                >
                                  <PlayCircle className={`mr-2 ${isSmallMobile ? 'h-3 w-3' : 'h-4 w-4'}`} /> Practice
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {attemptMode === 'all' && renderQuestionsToAttempt()}

          {attemptMode === 'review' && (
            <div className="space-y-6">
              <Card>
                <CardHeader className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
                  <CardTitle className={`${isSmallMobile ? 'text-base' : ''}`}>Results</CardTitle>
                  <CardDescription className={`${isSmallMobile ? 'text-xs' : ''}`}>
                    You scored {score} out of {questionsToAttempt.length} ({Math.round((score / questionsToAttempt.length) * 100)}%)
                  </CardDescription>
                </CardHeader>
                <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
                  <Progress value={(score / questionsToAttempt.length) * 100} className="h-2 mb-4" />
                  
                  {questionsToAttempt.map((question, qIndex) => (
                    <Card key={qIndex} className="mb-4">
                      <CardContent className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-4'}`}>
                        <div className="mb-2">
                          <span className="font-medium mr-2">Q{qIndex + 1}:</span>
                          <MathRenderer text={question.question} />
                        </div>
                        <div className="space-y-2">
                          {question.options.map((option, oIndex) => (
                            <div 
                              key={oIndex} 
                              className={`flex items-center p-2 rounded-md ${
                                question.correctanswer === oIndex 
                                  ? 'bg-green-100 dark:bg-green-900/20' 
                                  : userAnswers[qIndex] === oIndex
                                    ? 'bg-red-100 dark:bg-red-900/20'
                                    : ''
                              }`}
                            >
                              <span className="mr-2">
                                {['A', 'B', 'C', 'D'][oIndex]}.
                              </span>
                              <span className={`${isSmallMobile ? 'text-sm' : ''}`}>
                                <MathRenderer text={option} />
                              </span>
                              {question.correctanswer === oIndex && (
                                <Check className="ml-auto text-green-600 h-4 w-4" />
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {showExplanations && question.explanation && (
                          <div className="mt-4 bg-muted/30 p-3 rounded-md">
                            <p className="font-medium mb-1">Explanation:</p>
                            <div className="prose max-w-none">
                              <MathRenderer text={question.explanation.replace(/\n/g, '<br />')} />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
                <CardFooter className={`${isSmallMobile ? 'p-2' : isMobile ? 'p-3' : 'p-6'}`}>
                  <Button onClick={handleBackToGeneration} className="w-full">
                    Back to Question Generator
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="performance">
          {renderPerformanceMetrics()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PYQMultiplier;